import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { StageBadge, fmtAmount, chartPalette } from "@/lib/util";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Coins, CheckSquare, Clock, XCircle, TrendUp, Sparkle, ArrowClockwise, CheckCircle, MagnifyingGlass, ThumbsUp, Buildings } from "@phosphor-icons/react";
import {
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [counts, setCounts] = useState(null);
  const [recent, setRecent] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get("/payments/queue-counts").then((r) => setCounts(r.data)).catch(() => {});
    api.get("/payments", { params: { limit: 8 } }).then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  // Live global search — hits /payments with the q param backend-side
  useEffect(() => {
    const q = search.trim();
    if (!q) { setResults(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/payments", { params: { q, limit: 15 } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const trend = (kpis?.trend || []).map((t) => ({ month: t.month.slice(5), count: t.count }));
  const pie = (kpis?.paid_this_month || []).map((p, i) => ({ name: p.currency, value: p.total, count: p.count, fill: chartPalette[i % chartPalette.length] }));
  const stageBar = (kpis?.by_stage || []).map((s) => ({ stage: s.stage.replace("_", " "), count: s.count }));

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1700px]">
      <header className="mb-6 md:mb-8 flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full glass text-xs font-medium text-amber-700">
            <Sparkle size={11} weight="fill" /> Live overview
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Hello, <span className="bg-gradient-to-br from-amber-500 via-amber-400 to-slate-500 bg-clip-text text-transparent">{user?.name?.split(" ")[0]}</span>.
          </h1>
          <p className="text-sm text-slate-600 mt-2">Here&apos;s what&apos;s flowing through the pipeline today.</p>
        </div>
        {["requester", "admin"].includes(user?.role) && (
          <Link data-testid="dashboard-new-btn" to="/new" className="amber-pill inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl text-sm font-semibold">
            New request <ArrowRight size={14} weight="bold" />
          </Link>
        )}
      </header>

      {/* Payments by brand — this week by default. Uses paid-basis so it
          matches actual money-out, with a date picker for other windows. */}
      <BrandsThisWeek />

      {/* Global search — matches reference, vendor, amount, tx hash, wallet, payer vendor, remarks */}
      <section className="mb-6" data-testid="dashboard-search">
        <div className={`glass rounded-2xl shimmer-border transition-all ${results ? "shadow-lg" : ""}`}>
          <label className="flex items-center gap-3 px-4 py-3">
            <MagnifyingGlass size={18} weight="bold" className="text-amber-600 shrink-0" />
            <input
              data-testid="dashboard-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reference, vendor, amount, tx hash, wallet…"
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm placeholder:text-slate-500"
            />
            {searching && <span className="text-[10px] font-mono text-slate-400 shrink-0">searching…</span>}
            {search && !searching && (
              <button
                data-testid="dashboard-search-clear"
                onClick={() => setSearch("")}
                className="text-slate-400 hover:text-slate-700 shrink-0"
                aria-label="Clear search"
              >
                <XCircle size={18} weight="fill" />
              </button>
            )}
          </label>

          {results && (
            <div className="border-t border-white/50 max-h-[420px] overflow-y-auto" data-testid="dashboard-search-results">
              {results.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-500">
                  No matches for <span className="font-mono">&quot;{search}&quot;</span>.
                </div>
              ) : (
                <>
                  <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-white/40">
                    {results.length} match{results.length === 1 ? "" : "es"}
                  </div>
                  <ul className="divide-y divide-white/50">
                    {results.map((p) => (
                      <li key={p.id}>
                        <button
                          onClick={() => { setSearch(""); navigate(`/requests/${p.id}`); }}
                          data-testid={`dashboard-search-hit-${p.reference}`}
                          className="w-full text-left px-4 py-3 hover:bg-amber-50/70 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-mono text-xs font-bold text-amber-700">{p.reference}</span>
                                <StageBadge stage={p.stage} approvalStep={p.approval_step} />
                              </div>
                              <div className="text-sm font-semibold text-slate-800 truncate">{p.vendor_name}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                <span className="font-mono">{p.currency_code} {fmtAmount(p.net_amount)}</span>
                                {p.brand_name && <span>· {p.brand_name}</span>}
                                {p.payer_vendor_name && <span>· via {p.payer_vendor_name}</span>}
                                {p.tx_hash && (
                                  <span className="font-mono truncate max-w-[140px] text-emerald-700">tx {p.tx_hash.slice(0, 8)}…</span>
                                )}
                              </div>
                            </div>
                            <ArrowRight size={14} weight="bold" className="text-slate-400 shrink-0" />
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Action queue */}
      <section className="mb-8">
        <div className="overline mb-3">Your queue</div>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-4">
          <ActionCard icon={ThumbsUp} label="Awaiting my approval" value={counts?.my?.awaiting_my_approval} to="/queue?stage=pending_approval&mine=1" testid="kpi-my-approval" visible={user?.is_approver || user?.role === "admin" || user?.role === "approver"} tint="from-purple-200/60 to-transparent" />
          <ActionCard icon={Clock} label="Awaiting my audit" value={counts?.my?.awaiting_my_audit} to="/queue?stage=pending_audit&mine=1" testid="kpi-my-audit" visible={["auditor","admin"].includes(user?.role)} tint="from-amber-200/60 to-transparent" />
          <ActionCard icon={CheckSquare} label="Awaiting my payment" value={counts?.my?.awaiting_my_payment} to="/queue?stage=approved&mine=1" testid="kpi-my-payment" visible={["payer","admin"].includes(user?.role)} tint="from-sky-200/60 to-transparent" />
          <ActionCard icon={TrendUp} label="Awaiting verification" value={counts?.my?.awaiting_my_verification} to="/queue?stage=paid&mine=1" testid="kpi-my-verify" visible={["payer","admin"].includes(user?.role)} tint="from-emerald-200/60 to-transparent" />
          <ActionCard icon={XCircle} label="My rejected" value={counts?.my?.my_rejected} to="/queue?stage=rejected" testid="kpi-my-rejected" visible={["requester","admin"].includes(user?.role)} tint="from-amber-200/60 to-transparent" />
        </div>
      </section>

      <PendingApprovalSection user={user} />

      {/* Charts row */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 glass rounded-3xl p-4 sm:p-6 shimmer-border">
          <div className="flex items-baseline justify-between mb-3 gap-2">
            <div className="min-w-0">
              <div className="overline">Trend</div>
              <div className="font-heading font-bold text-base sm:text-xl truncate">Requests created — last 6 months</div>
            </div>
            <TrendUp size={22} weight="duotone" className="text-amber-500 shrink-0" />
          </div>
          <div className="h-56 sm:h-64">
            <ResponsiveContainer>
              <AreaChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRose" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#D4A017" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#D4A017" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,180,190,0.35)" />
                <XAxis dataKey="month" stroke="#7c6b76" fontSize={11} />
                <YAxis stroke="#7c6b76" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #D4A01744", borderRadius: 12, backdropFilter: "blur(10px)" }} />
                <Area type="monotone" dataKey="count" stroke="#D4A017" strokeWidth={2.5} fill="url(#gRose)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
          <div className="overline mb-2">Paid this month</div>
          <div className="font-heading font-bold text-base sm:text-xl mb-2">By currency</div>
          {pie.length === 0 ? (
            <div className="h-56 sm:h-64 flex items-center justify-center text-sm text-slate-500 text-center">
              <div>
                <Coins size={22} className="mx-auto mb-2 text-slate-400" />
                No payments this month yet.
              </div>
            </div>
          ) : (
            <div className="h-56 sm:h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={4}>
                    {pie.map((entry, i) => <Cell key={i} fill={entry.fill} stroke="rgba(255,255,255,0.9)" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #D4A01744", borderRadius: 12 }}
                           formatter={(v, n, p) => [fmtAmount(v, p.payload.name), `${p.payload.count} txn(s)`]} />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* Pipeline bar chart */}
      <section className="mb-8">
        <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="overline">Pipeline</div>
              <div className="font-heading font-bold text-base sm:text-xl">Requests by stage</div>
            </div>
          </div>
          <div className="h-48 sm:h-56">
            <ResponsiveContainer>
              <BarChart data={stageBar} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gBar" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#F5C842" />
                    <stop offset="100%" stopColor="#B8B8C5" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,180,190,0.3)" />
                <XAxis dataKey="stage" stroke="#7c6b76" fontSize={11} />
                <YAxis stroke="#7c6b76" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #D4A01744", borderRadius: 12 }} />
                <Bar dataKey="count" fill="url(#gBar)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Payments by brand — all-time paid+verified totals per currency per brand */}
      <section className="mb-8">
        <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border" data-testid="payments-by-brand">
          <div className="flex items-baseline justify-between mb-4 gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="overline">Vendors paid</div>
              <div className="font-heading font-bold text-base sm:text-xl">Payments done by brand</div>
            </div>
            <div className="text-xs text-slate-500">All-time · paid + verified</div>
          </div>
          {(!kpis?.by_brand || kpis.by_brand.length === 0) ? (
            <div className="text-sm text-slate-500 text-center py-8">
              <Coins size={22} className="mx-auto mb-2 text-slate-400" />
              No payments completed yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ledger-table text-sm">
                <thead className="bg-white/40">
                  <tr className="text-left">
                    <th className="px-4 py-2 overline">Brand</th>
                    <th className="px-4 py-2 overline text-right">Payments</th>
                    <th className="px-4 py-2 overline">Totals by currency</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.by_brand.map((b) => (
                    <tr key={b.brand_id} data-testid={`by-brand-row-${b.brand_name}`}>
                      <td className="px-4 py-3 font-semibold">{b.brand_name}</td>
                      <td className="px-4 py-3 text-right font-mono">{b.count}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(b.totals || {}).sort().map(([cur, amt]) => (
                            <span key={cur} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-amber-50 border border-amber-200 text-amber-900">
                              <span className="font-mono font-bold">{fmtAmount(amt)}</span>
                              <span className="font-semibold">{cur}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* AI insights (admin & auditor) */}
      {(user?.role === "admin" || user?.role === "auditor") && (
        <section className="mb-8">
          <AIInsights />
        </section>
      )}

      {/* Recent */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div className="overline">Recent activity</div>
          <Link to="/queue" data-testid="recent-view-all" className="text-xs font-semibold text-amber-600 hover:underline">View all →</Link>
        </div>
        {/* Mobile card list */}
        <div className="md:hidden space-y-3">
          {recent.map((p) => (
            <Link
              key={p.id}
              to={`/requests/${p.id}`}
              data-testid={`recent-card-${p.reference}`}
              className="block glass rounded-2xl p-4 shimmer-border"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-amber-700 font-bold truncate">{p.reference}</div>
                  <div className="text-[10px] text-slate-500 font-mono mt-0.5">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <StageBadge stage={p.stage} approvalStep={p.approval_step} />
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900">
                  {p.brand_name}
                </span>
                <span className="text-sm font-semibold text-slate-800 truncate">{p.vendor_name}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono font-black text-base text-slate-900 truncate">
                  {fmtAmount(p.net_amount, p.currency_code)}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
                  {p.payment_mode}
                  {p.payment_mode === "crypto" && p.blockchain?.verified && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                      <CheckCircle size={9} weight="fill" /> ON-CHAIN
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
          {recent.length === 0 && (
            <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No requests yet.</div>
          )}
        </div>

        <div className="hidden md:block glass rounded-3xl overflow-x-auto">
          <table className="w-full text-sm ledger-table">
            <thead className="bg-white/40">
              <tr className="text-left">
                {["Ref","Created","Brand","Vendor","Amount","Mode","Requester","Auditor","Payer","Stage",""].map((h) => (
                  <th key={h} className="px-4 py-3 overline">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((p) => (
                <tr key={p.id} data-testid={`recent-row-${p.reference}`} className="hover:bg-white/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-amber-600 font-semibold">{p.reference}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{p.brand_name}</td>
                  <td className="px-4 py-3">{p.vendor_name}</td>
                  <td className="px-4 py-3 font-mono font-semibold">{fmtAmount(p.net_amount, p.currency_code)}</td>
                  <td className="px-4 py-3 uppercase text-xs">
                    <div className="inline-flex items-center gap-1.5">
                      {p.payment_mode}
                      {p.payment_mode === "crypto" && p.blockchain?.verified && (
                        <span
                          data-testid={`recent-chain-verified-${p.reference}`}
                          title="Blockchain verified on chain"
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300"
                        >
                          <CheckCircle size={9} weight="fill" /> ON-CHAIN
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3"><MiniWho name={p.requester_name} tint="slate" /></td>
                  <td className="px-4 py-3"><MiniWho name={p.audited_by_name} tint="sky" /></td>
                  <td className="px-4 py-3">
                    {p.paid_by_name ? (
                      <MiniWho name={p.paid_by_name} tint="emerald" />
                    ) : p.assigned_payer_name ? (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase">→</span>
                        <MiniWho name={p.assigned_payer_name} tint="amber" />
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StageBadge stage={p.stage} approvalStep={p.approval_step} /></td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/requests/${p.id}`} className="text-xs font-semibold text-amber-600 hover:underline" data-testid={`recent-open-${p.reference}`}>Open →</Link>
                  </td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-500">No requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AIInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/ai/insights", { params: force ? { refresh: true } : {} });
      setData(r.data);
      if (!r.data.ok) setErr(r.data.error || "AI unavailable");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(false); }, []);

  const genAt = data?.generated_at ? new Date(data.generated_at) : null;

  return (
    <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(245, 200, 66, 0.6), transparent)" }} />
      <div className="relative flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/70 border border-amber-200 text-xs font-medium text-amber-700">
            <Sparkle size={11} weight="fill" /> AI insights · Claude Sonnet 4.5
          </div>
          <div className="font-heading font-bold text-base sm:text-xl">Operational intelligence</div>
          {data?.headline && <p className="text-sm text-slate-700 mt-1">{data.headline}</p>}
          {genAt && (
            <div className="relative text-xs text-slate-500 mt-1 font-mono">
              Generated {timeAgo(genAt)} {data?.cached ? "· cached" : "· fresh"}
            </div>
          )}
        </div>
        <button data-testid="ai-insights-run" onClick={() => load(true)} disabled={loading || refreshing}
          className="amber-pill inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-60 whitespace-nowrap">
          {(loading || refreshing) ? <ArrowClockwise size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
          {loading ? "Loading…" : refreshing ? "Analyzing…" : (data?.ok ? "Refresh" : "Generate")}
        </button>
      </div>

      {loading && !data && <InsightsSkeleton />}

      {err && !loading && (
        <div className="relative rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-xs">
          <div className="font-semibold text-amber-900 mb-1">Couldn&apos;t generate insights</div>
          <div className="text-amber-800 font-mono break-all">{err}</div>
          {err.toLowerCase().includes("anthropic_api_key not configured") && (
            <div className="mt-3 text-amber-900 leading-relaxed font-sans">
              <div className="font-semibold mb-1">Set your Anthropic key to enable AI Insights:</div>
              <div className="text-slate-700 space-y-1">
                <div>1. Grab a key at <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="underline text-amber-800 font-semibold">console.anthropic.com</a>.</div>
                <div>2. Go to <Link to="/admin" className="underline text-amber-800 font-semibold">Admin → AI</Link> and paste it in — takes 5 seconds, no restart needed.</div>
              </div>
            </div>
          )}
          <button onClick={() => load(true)} className="mt-3 text-amber-900 underline text-xs font-semibold">Try again</button>
        </div>
      )}

      {data?.ok && (
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <InsightList title="Highlights" items={data.highlights} accent="emerald" />
          <InsightList title="Risks" items={data.risks} accent="rose" />
          <InsightList title="Suggestions" items={data.suggestions} accent="amber" />
        </div>
      )}
    </div>
  );
}
function MiniWho({ name, tint }) {
  if (!name) return <span className="text-xs text-slate-400">—</span>;
  const color = { slate: "text-slate-700", sky: "text-sky-700", emerald: "text-emerald-700", amber: "text-amber-700" }[tint] || "text-slate-700";
  const dot = { slate: "bg-slate-400", sky: "bg-sky-400", emerald: "bg-emerald-400", amber: "bg-amber-400" }[tint] || "bg-slate-400";
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
      <span className="truncate max-w-[110px]">{name}</span>
    </div>
  );
}



function InsightsSkeleton() {
  return (
    <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-2xl bg-white/60 border border-amber-100 p-4 space-y-2">
          <div className="h-3 w-20 rounded-md bg-amber-100 animate-pulse" />
          {[0, 1, 2, 3].map((j) => (
            <div key={j} className="h-2.5 rounded-md bg-slate-100 animate-pulse"
              style={{ width: `${60 + ((i + j) * 7) % 40}%`, animationDelay: `${(i * 100) + j * 60}ms` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function timeAgo(d) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function InsightList({ title, items, accent }) {
  const color = { emerald: "text-emerald-700", rose: "text-rose-700", amber: "text-amber-700" }[accent] || "text-slate-700";
  return (
    <div className="rounded-2xl bg-white/60 border border-amber-100 p-4">
      <div className={`overline mb-2 ${color}`}>{title}</div>
      <ul className="space-y-1.5 text-xs text-slate-700">
        {(items || []).map((s, i) => <li key={i} className="flex gap-1.5"><span className={`${color} font-black`}>•</span><span>{s}</span></li>)}
        {(!items || items.length === 0) && <li className="text-slate-500">—</li>}
      </ul>
    </div>
  );
}

function ActionCard({ icon: Icon, label, value, to, testid, visible = true, tint }) {
  if (!visible) return null;
  return (
    <Link to={to} data-testid={testid} className="relative glass rounded-2xl sm:rounded-3xl p-3 sm:p-5 group hover:shadow-2xl transition-all shimmer-border overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-br ${tint} pointer-events-none opacity-70`} />
      <div className="relative flex items-start justify-between">
        <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl chrome flex items-center justify-center">
          <Icon size={16} weight="duotone" className="text-amber-500 sm:w-5 sm:h-5" />
        </div>
        <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-amber-500" />
      </div>
      <div className="relative font-heading font-black text-2xl sm:text-4xl mt-2 sm:mt-5 bg-gradient-to-br from-slate-800 to-amber-600 bg-clip-text text-transparent">{value ?? 0}</div>
      <div className="relative text-[11px] sm:text-xs text-slate-700 font-medium mt-0.5 sm:mt-1 leading-tight">{label}</div>
    </Link>
  );
}

/* --- Pending approval — visible to admins (all rows) and users with the
   is_approver flag (only rows assigned to them). Regular requesters see
   their OWN pending-approval requests here so they can see who's holding it. */
function PendingApprovalSection({ user }) {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const isApprover = !!user?.is_approver;
  const isAdmin = user?.role === "admin";
  const canApprove = isApprover || isAdmin;

  const load = useCallback(async () => {
    try {
      const params = { stage: "pending_approval", limit: 100 };
      const r = await api.get("/payments", { params });
      // Filter rules:
      //  - is_approver / approver role → only what's currently on their plate
      //  - admin → assigned to them OR unassigned (fallback owner)
      //  - anyone else → their own submissions only, so they can watch progress
      let filtered;
      if (isApprover) {
        filtered = r.data.filter((p) => p.assigned_approver_id === user?.id
          || (p.prior_approver_ids || []).includes(user?.id));
      } else if (isAdmin) {
        filtered = r.data.filter((p) => !p.assigned_approver_id || p.assigned_approver_id === user?.id);
      } else {
        filtered = r.data.filter((p) => p.requester_id === user?.id);
      }
      setRows(filtered);
      setSelected((prev) => new Set([...prev].filter((id) => filtered.some((p) => p.id === id))));
    } catch {
      setRows([]);
    }
  }, [isApprover, isAdmin, user?.id]);

  useEffect(() => { load(); }, [load]);

  // Approvers can only ACT on rows assigned to them (admins can act on all).
  const canActOn = (p) => isAdmin || (isApprover && p.assigned_approver_id === user?.id);
  const actionable = (rows || []).filter(canActOn);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === actionable.length) setSelected(new Set());
    else setSelected(new Set(actionable.map((p) => p.id)));
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Approve ${selected.size} request${selected.size === 1 ? "" : "s"}? They will move to the auditor queue.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/bulk-approver-approve", { ids: [...selected] });
      const skippedNonAssigned = (data.skipped || []).filter((s) => s.reason === "not_assigned").length;
      if (data.approved_count > 0) {
        toast.success(`Approved ${data.approved_count} request${data.approved_count === 1 ? "" : "s"}. They're now with the auditor.`);
      }
      if (skippedNonAssigned > 0) {
        toast.warning(`${skippedNonAssigned} skipped — not assigned to you.`);
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Bulk approve failed");
    } finally {
      setBusy(false);
    }
  };

  const bulkReject = async () => {
    if (selected.size === 0) return;
    const reason = window.prompt(`Reject ${selected.size} request${selected.size === 1 ? "" : "s"}? Enter a shared reason (applied to all):`);
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A rejection reason is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/bulk-approver-reject", { ids: [...selected], reason: trimmed });
      const skippedNonAssigned = (data.skipped || []).filter((s) => s.reason === "not_assigned").length;
      if (data.rejected_count > 0) {
        toast.success(`Rejected ${data.rejected_count} request${data.rejected_count === 1 ? "" : "s"}.`);
      }
      if (skippedNonAssigned > 0) {
        toast.warning(`${skippedNonAssigned} skipped — not assigned to you.`);
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Bulk reject failed");
    } finally {
      setBusy(false);
    }
  };

  // Single approve — called from swipe-left gesture on mobile card.
  const singleApprove = async (p) => {
    try {
      await api.post(`/payments/${p.id}/approver-approve`, { note: "Approved via swipe" });
      toast.success(`Approved ${p.reference}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Approve failed");
    }
  };

  // Single reject — called from swipe-right. Reason is required.
  const singleReject = async (p) => {
    const reason = window.prompt(`Reject ${p.reference}? Enter a reason:`);
    if (reason === null) return;                // user cancelled
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A reason is required to reject."); return; }
    try {
      await api.post(`/payments/${p.id}/approver-reject`, { note: trimmed });
      toast.success(`Rejected ${p.reference}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Reject failed");
    }
  };

  if (rows === null) return null;                  // still loading — silent
  if (rows.length === 0) return null;              // nothing to show — hide

  const heading = canApprove ? "Awaiting approval" : "Your requests waiting for approval";
  const subLabel = canApprove
    ? (isAdmin ? "Assigned to you or unassigned — take the ones nobody else picked up" : "Assigned to you plus everything you've already touched")
    : "You'll see them here until the assigned approver acts";

  return (
    <section className="mb-8" data-testid="pending-approval-section">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="overline">{heading}</div>
          <div className="text-xs text-slate-500 mt-0.5">{subLabel}</div>
        </div>
        {canApprove && selected.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={bulkReject}
              disabled={busy}
              data-testid="pending-approval-bulk-reject-btn"
              className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-red-100 text-red-800 border border-red-300 text-sm font-bold uppercase tracking-wider hover:bg-red-200 disabled:opacity-50"
            >
              <XCircle size={16} weight="fill" /> Reject {selected.size}
            </button>
            <button
              onClick={bulkApprove}
              disabled={busy}
              data-testid="pending-approval-bulk-btn"
              className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-emerald-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"
            >
              <ThumbsUp size={16} weight="fill" /> Approve {selected.size}
            </button>
          </div>
        )}
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2.5" data-testid="pending-approval-mobile">
        {canApprove && actionable.length > 1 && (
          <label className="flex items-center gap-2 px-3 text-xs text-slate-600">
            <input
              type="checkbox"
              className="w-4 h-4 accent-emerald-600"
              checked={actionable.length > 0 && selected.size === actionable.length}
              onChange={toggleAll}
              data-testid="pending-approval-select-all-mob"
            />
            Select all mine ({actionable.length})
          </label>
        )}
        {rows.map((p) => {
          const mine = canActOn(p);
          return (
            <PendingApprovalMobileCard
              key={p.id}
              p={p}
              canAct={canApprove && mine}
              checked={selected.has(p.id)}
              onToggle={() => toggleRow(p.id)}
              currentUserIsAssigned={mine}
              onApprove={singleApprove}
              onReject={singleReject}
            />
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto shimmer-border">
        <table className="w-full text-sm">
          <thead className="bg-white/40">
            <tr className="text-left">
              {canApprove && (
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-emerald-600"
                    checked={actionable.length > 0 && selected.size === actionable.length}
                    onChange={toggleAll}
                    disabled={actionable.length === 0}
                    data-testid="pending-approval-select-all"
                  />
                </th>
              )}
              {["Ref", "Brand", "Vendor", "Amount", "Currency", "Awaiting approval from", "Requested by"].map((h) => (
                <th key={h} className="px-3 py-2 overline">{h}</th>
              ))}
              {canApprove && <th className="px-3 py-2 overline text-right">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/40">
            {rows.map((p) => {
              const mine = canActOn(p);
              return (
                <tr key={p.id} data-testid={`pending-approval-row-${p.reference}`} className={selected.has(p.id) ? "bg-emerald-50/60" : "hover:bg-amber-50/40"}>
                  {canApprove && (
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-emerald-600"
                        checked={selected.has(p.id)}
                        disabled={!mine}
                        onChange={() => toggleRow(p.id)}
                        data-testid={`pending-approval-select-${p.reference}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5 font-mono text-xs">
                    <Link to={`/requests/${p.id}`} className="text-amber-700 font-bold hover:underline">{p.reference}</Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900">{p.brand_name}</span>
                  </td>
                  <td className="px-3 py-2.5 font-medium truncate max-w-[160px]">{p.vendor_name}</td>
                  <td className="px-3 py-2.5 font-mono font-semibold">{fmtAmount(p.net_amount)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{p.currency_code}</td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className={`font-semibold ${mine ? "text-emerald-700" : "text-slate-600"}`}>
                      {p.assigned_approver_name || "—"}
                      {mine && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-emerald-600">(you)</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{p.requester_name}</td>
                  {canApprove && (
                    <td className="px-3 py-2.5 text-right">
                      <Link
                        to={`/requests/${p.id}`}
                        data-testid={`pending-approval-open-${p.reference}`}
                        className="text-xs font-semibold text-amber-700 hover:underline"
                      >
                        Open →
                      </Link>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PendingApprovalMobileCard({ p, canAct, checked, onToggle, currentUserIsAssigned, onApprove, onReject }) {
  const [dragX, setDragX] = useState(0);
  const [startX, setStartX] = useState(null);
  const REVEAL_THRESHOLD = 60;   // px offset before actions peek
  const COMMIT_THRESHOLD = 130;  // px offset before releasing = commit
  const MAX_DRAG = 180;

  const onTouchStart = (e) => {
    if (!canAct) return;
    setStartX(e.touches[0].clientX);
  };
  const onTouchMove = (e) => {
    if (startX === null) return;
    const dx = e.touches[0].clientX - startX;
    const clamped = Math.max(-MAX_DRAG, Math.min(MAX_DRAG, dx));
    setDragX(clamped);
  };
  const onTouchEnd = () => {
    if (startX === null) return;
    setStartX(null);
    if (dragX <= -COMMIT_THRESHOLD) {
      // Swipe left committed = APPROVE
      setDragX(-MAX_DRAG);
      setTimeout(() => { setDragX(0); onApprove?.(p); }, 150);
    } else if (dragX >= COMMIT_THRESHOLD) {
      // Swipe right committed = REJECT
      setDragX(MAX_DRAG);
      setTimeout(() => { setDragX(0); onReject?.(p); }, 150);
    } else {
      setDragX(0);
    }
  };

  const abs = Math.abs(dragX);
  const showApprove = dragX < -REVEAL_THRESHOLD;
  const showReject = dragX > REVEAL_THRESHOLD;

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Background action layers */}
      {canAct && (
        <>
          <div
            className={`absolute inset-y-0 right-0 flex items-center gap-2 px-5 bg-emerald-500 text-white font-bold uppercase tracking-wider text-sm transition-opacity ${showApprove ? "opacity-100" : "opacity-60"}`}
            style={{ width: `${Math.min(MAX_DRAG, abs)}px` }}
          >
            <ThumbsUp size={18} weight="fill" />
            {abs >= COMMIT_THRESHOLD ? "Release to approve" : "Approve"}
          </div>
          <div
            className={`absolute inset-y-0 left-0 flex items-center gap-2 px-5 bg-red-500 text-white font-bold uppercase tracking-wider text-sm justify-end transition-opacity ${showReject ? "opacity-100" : "opacity-60"}`}
            style={{ width: `${Math.min(MAX_DRAG, abs)}px`, flexDirection: "row-reverse" }}
          >
            <XCircle size={18} weight="fill" />
            {abs >= COMMIT_THRESHOLD ? "Release to reject" : "Reject"}
          </div>
        </>
      )}

      {/* Foreground draggable card */}
      <div
        data-testid={`pending-approval-card-${p.reference}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: startX === null ? "transform 150ms ease-out" : "none",
          touchAction: "pan-y",
        }}
        className={`relative glass rounded-2xl p-4 shimmer-border transition-colors ${checked ? "ring-2 ring-emerald-400 bg-emerald-50/50" : ""}`}
      >
        <div className="flex items-start gap-3">
          {canAct && (
            <input
              type="checkbox"
              checked={checked}
              onChange={onToggle}
              className="mt-1 w-5 h-5 accent-emerald-600 shrink-0"
              data-testid={`pending-approval-check-mob-${p.reference}`}
            />
          )}
          <Link to={`/requests/${p.id}`} className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-mono text-xs font-bold text-amber-700">{p.reference}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 border border-purple-300 text-purple-800">Pending approval</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900">{p.brand_name}</span>
              <span className="text-sm font-semibold text-slate-800 truncate">{p.vendor_name}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-mono font-black text-base text-slate-900">
                {p.currency_code} {fmtAmount(p.net_amount)}
              </div>
            </div>
            <div className="text-[11px] leading-relaxed pt-2 border-t border-white/50">
              <div className={`font-semibold ${currentUserIsAssigned ? "text-emerald-700" : "text-slate-600"}`}>
                Awaiting: {p.assigned_approver_name || "—"}
                {currentUserIsAssigned && <span className="ml-1 text-[9px] uppercase tracking-wider text-emerald-600">(you)</span>}
              </div>
              <div className="text-slate-500 mt-0.5">Requested by {p.requester_name}</div>
              {canAct && (
                <div className="text-[10px] text-slate-400 mt-1.5 italic">
                  ← swipe left to approve · swipe right to reject →
                </div>
              )}
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}


// Payments by brand · defaults to Mon–today of the current week (paid basis
// so it reflects actual money out). Editable date range; each brand row links
// through to the fully-filtered Queue.
function BrandsThisWeek() {
  const weekStart = (() => {
    const d = new Date();
    const dow = d.getDay(); // 0=Sun, 1=Mon...
    const diff = dow === 0 ? 6 : dow - 1;
    d.setDate(d.getDate() - diff);
    return d.toISOString().slice(0, 10);
  })();
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(weekStart);
  const [to, setTo] = useState(today);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { basis: "paid" };
    if (from) params.date_from = from;
    if (to) params.date_to = `${to}T23:59:59`;
    api.get("/reports/by-brand", { params })
      .then((r) => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [from, to]);

  const currencies = Array.from(new Set(data.flatMap((r) => Object.keys(r.spent || {})))).sort();
  const grandTotals = {};
  let grandCount = 0;
  for (const row of data) {
    grandCount += row.count || 0;
    for (const c of currencies) grandTotals[c] = (grandTotals[c] || 0) + (row.spent?.[c] || 0);
  }

  const drillUrl = (row) => {
    const p = new URLSearchParams({
      brand: row.key, stage_in: "paid,verified", basis: "paid",
    });
    if (from) p.set("date_from", from);
    if (to)   p.set("date_to",   to);
    return `/queue?${p.toString()}`;
  };

  return (
    <section className="mb-6" data-testid="dashboard-brands">
      <div className="glass rounded-2xl p-4 sm:p-5 shimmer-border">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="overline flex items-center gap-1.5"><Buildings size={12} weight="fill" className="text-amber-700" /> Payments by brand</div>
            <div className="font-heading font-bold text-lg sm:text-xl">
              {from === weekStart && to === today ? "This week" : "Custom range"}
              <span className="ml-2 text-xs font-mono text-slate-500 font-normal">{from} → {to}</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold pl-1 block">Paid from</label>
              <input data-testid="dashboard-brands-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined}
                     className="rounded-xl bg-white/70 border border-white/60 h-9 px-2 text-sm w-36 outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold pl-1 block">Paid to</label>
              <input data-testid="dashboard-brands-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined}
                     className="rounded-xl bg-white/70 border border-white/60 h-9 px-2 text-sm w-36 outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
            <button data-testid="dashboard-brands-week" onClick={() => { setFrom(weekStart); setTo(today); }}
                    className="h-9 px-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold hover:bg-amber-100 whitespace-nowrap">
              This week
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-sm text-slate-500 py-6">Loading…</div>
        ) : data.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-6">
            No payments in this range yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-3 py-2 overline">Brand</th>
                  <th className="px-3 py-2 overline text-right">Paid requests</th>
                  {currencies.map((c) => (
                    <th key={c} className="px-3 py-2 overline text-right">{c} paid</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.key} data-testid={`dashboard-brand-${row.label}`} className="hover:bg-amber-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <Link to={drillUrl(row)} className="font-semibold text-amber-800 hover:underline hover:text-amber-950 inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900">{row.label}</span>
                        <ArrowRight size={11} weight="bold" className="opacity-40" />
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{row.count}</td>
                    {currencies.map((c) => (
                      <td key={c} className="px-3 py-2 text-right font-mono font-semibold text-emerald-800">
                        {row.spent?.[c] ? fmtAmount(row.spent[c]) : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr data-testid="dashboard-brands-total" className="bg-amber-100/50 border-t-2 border-amber-300 font-bold">
                  <td className="px-3 py-2 uppercase tracking-wider text-xs text-amber-900">Total</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-900">{grandCount}</td>
                  {currencies.map((c) => (
                    <td key={"g-" + c} className="px-3 py-2 text-right font-mono text-emerald-900">
                      {grandTotals[c] ? fmtAmount(grandTotals[c]) : "—"}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

